/**
 * Server-side API Client
 *
 * A simplified API client for server actions that handles
 * authentication and provides a clean interface.
 */

import { getApiUrl } from '@/lib/config/environment';

// ============================================================================
// Types
// ============================================================================

/**
 * Query parameters for API requests
 */
type QueryParams = object;

/**
 * Error details from API responses
 */
interface ApiErrorDetails {
  message?: string;
  error?: string;
  [key: string]: unknown;
}

export interface RequestOptions extends RequestInit {
  params?: QueryParams;
}

export class ApiClientError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public details?: ApiErrorDetails | Error
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

// ============================================================================
// API Client
// ============================================================================

class ServerApiClient {
  private baseUrl: string;

  constructor() {
    // Use environment configuration
    const apiUrl = getApiUrl();
    // During build or when using mock data, use a placeholder URL
    this.baseUrl = apiUrl || 'http://mock-api';
  }

  /**
   * Make a GET request
   */
  async get<T>(path: string, options?: RequestOptions): Promise<T> {
    return this.request<T>('GET', path, undefined, options);
  }

  /**
   * Make a POST request
   */
  async post<T, TBody = unknown>(
    path: string,
    data?: TBody,
    options?: RequestOptions
  ): Promise<T> {
    return this.request<T>('POST', path, data, options);
  }

  /**
   * Make a PUT request
   */
  async put<T, TBody = unknown>(
    path: string,
    data?: TBody,
    options?: RequestOptions
  ): Promise<T> {
    return this.request<T>('PUT', path, data, options);
  }

  /**
   * Make a PATCH request
   */
  async patch<T, TBody = unknown>(
    path: string,
    data?: TBody,
    options?: RequestOptions
  ): Promise<T> {
    return this.request<T>('PATCH', path, data, options);
  }

  /**
   * Make a DELETE request
   */
  async delete<T = void>(path: string, options?: RequestOptions): Promise<T> {
    return this.request<T>('DELETE', path, undefined, options);
  }

  /**
   * Core request method
   */
  private async request<T>(
    method: string,
    path: string,
    data?: unknown,
    options?: RequestOptions
  ): Promise<T> {
    const url = new URL(path, this.baseUrl);

    // Add query parameters if provided
    if (options?.params) {
      Object.entries(options.params as Record<string, unknown>).forEach(
        ([key, value]) => {
          if (value !== undefined && value !== null) {
            url.searchParams.append(key, String(value));
          }
        }
      );
    }

    // Separate headers from other options to prevent overwriting
    const { headers: optionHeaders, ...restOptions } = options || {};

    const requestOptions: RequestInit = {
      method,
      ...restOptions,
      headers: {
        'Content-Type': 'application/json',
        ...optionHeaders
      }
    };

    // Add body for methods that support it
    if (data && ['POST', 'PUT', 'PATCH'].includes(method)) {
      requestOptions.body = JSON.stringify(data);

      // Log body for debugging tag operations
      if (process.env.NODE_ENV === 'development' && path.includes('/tags')) {
        console.log('[API Client] Request to backend:', {
          url: url.toString(),
          method,
          body: requestOptions.body,
          parsedBody: data
        });
      }
    }

    try {
      // API Request being made

      const response = await fetch(url.toString(), requestOptions);

      // API Response received

      // Handle non-OK responses
      if (!response.ok) {
        let errorData;
        let errorText;
        try {
          errorText = await response.text();
          errorData = JSON.parse(errorText);
        } catch {
          // Failed to parse error response
          errorData = { message: errorText || response.statusText };
        }

        // API Error details available in errorData

        // 500 errors available in errorData

        throw new ApiClientError(
          errorData.message ||
            errorData.error ||
            `HTTP ${response.status} error`,
          response.status,
          errorData
        );
      }

      // Handle empty responses
      if (
        response.status === 204 ||
        response.headers.get('content-length') === '0'
      ) {
        return {} as T;
      }

      // Parse JSON response
      const result = await response.json();
      return result as T;
    } catch (error) {
      // Re-throw ApiClientError
      if (error instanceof ApiClientError) {
        throw error;
      }

      // Handle fetch errors
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new ApiClientError(
          'Network error: Unable to connect to the server',
          undefined,
          error
        );
      }

      // Handle other errors
      throw new ApiClientError(
        error instanceof Error ? error.message : 'Unknown error occurred',
        undefined,
        error instanceof Error ? error : undefined
      );
    }
  }
}

// Export singleton instance with lazy initialization
let apiClientInstance: ServerApiClient | null = null;

export const apiClient = {
  get<T>(path: string, options?: RequestOptions): Promise<T> {
    if (!apiClientInstance) {
      apiClientInstance = new ServerApiClient();
    }

    return apiClientInstance.get<T>(path, options);
  },

  post<T, TBody = unknown>(
    path: string,
    data?: TBody,
    options?: RequestOptions
  ): Promise<T> {
    if (!apiClientInstance) {
      apiClientInstance = new ServerApiClient();
    }

    return apiClientInstance.post<T>(path, data, options);
  },

  put<T, TBody = unknown>(
    path: string,
    data?: TBody,
    options?: RequestOptions
  ): Promise<T> {
    if (!apiClientInstance) {
      apiClientInstance = new ServerApiClient();
    }

    return apiClientInstance.put<T>(path, data, options);
  },

  patch<T, TBody = unknown>(
    path: string,
    data?: TBody,
    options?: RequestOptions
  ): Promise<T> {
    if (!apiClientInstance) {
      apiClientInstance = new ServerApiClient();
    }

    return apiClientInstance.patch<T>(path, data, options);
  },

  delete<T>(path: string, options?: RequestOptions): Promise<T> {
    if (!apiClientInstance) {
      apiClientInstance = new ServerApiClient();
    }

    return apiClientInstance.delete<T>(path, options);
  }
};
